import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

export default function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Scroll to top functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const megaMenuServices = [
    {
      category: 'Smartphone & Multimedia',
      icon: 'ri-smartphone-line',
      color: 'from-cyan-400 to-blue-500',
      link: '/diensten/smartphone-multimedia',
      services: [
        { name: 'CarPlay & Android Auto', icon: 'ri-phone-line' },
        { name: 'Android Schermen', icon: 'ri-tablet-line' }
      ]
    },
    {
      category: 'Interieur Upgrades',
      icon: 'ri-star-line',
      color: 'from-purple-400 to-pink-500',
      link: '/diensten/sterrenhemel-sfeerverlichting-achteruitcamera-in-antwerpen',
      services: [
        { name: 'Sterrenhemel', icon: 'ri-star-fill' },
        { name: 'Sfeerverlichting', icon: 'ri-lightbulb-line' },
        { name: 'Achteruitrijcamera', icon: 'ri-camera-line' }
      ]
    }
  ];

  return (
    <>
      {/* Appointment Notice Bar - Alleen zichtbaar bovenaan */}
      <div className={`fixed top-0 left-0 right-0 bg-black text-white py-2 px-4 text-center z-[101] border-b border-white/10 transition-transform duration-300 ${
        isScrolled ? '-translate-y-full' : 'translate-y-0'
      }`}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center space-x-2 text-xs sm:text-sm font-medium">
            <i className="ri-calendar-check-line text-base sm:text-lg"></i>
            <span className="hidden sm:inline">⚠️ Enkel op afspraak - Bel 0489 17 05 92 voor een afspraak</span>
            <span className="sm:hidden">⚠️ Enkel op afspraak</span>
            <a 
              href="tel:0489170592" 
              className="ml-2 sm:ml-4 px-3 sm:px-4 py-1 sm:py-1.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-xs sm:text-sm rounded-full hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer font-medium"
            >
              Bel Nu
            </a>
          </div>
        </div>
      </div>

      {/* Main Navigation - Beweegt naar boven wanneer je scrollt */}
      <nav 
        className={`fixed left-0 right-0 transition-all duration-300 ${
          isScrolled 
            ? 'top-0 bg-black/95 backdrop-blur-xl shadow-2xl shadow-cyan-500/10' 
            : 'top-[40px] bg-gradient-to-b from-black/80 to-transparent backdrop-blur-sm'
        } z-[100]`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg blur-lg opacity-0 group-hover:opacity-75 transition-opacity duration-500"></div>
                <img 
                  src="https://static.readdy.ai/image/9e62058216dff63d1a686833c97014ce/62aa69866537ce3dcbdf691bac32eaca.png" 
                  alt="DSF Cartech Logo" 
                  className="h-12 sm:h-16 w-auto relative z-10 transition-transform duration-500 group-hover:scale-110"
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-1">
              <Link
                to="/"
                className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 hover:scale-105 whitespace-nowrap ${
                  location.pathname === '/'
                    ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                Home
              </Link>

              <div 
                className="relative"
                onMouseEnter={() => setShowMegaMenu(true)}
                onMouseLeave={() => setShowMegaMenu(false)}
              >
                <Link
                  to="/diensten"
                  className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 hover:scale-105 whitespace-nowrap flex items-center gap-2 ${
                    location.pathname.startsWith('/diensten')
                      ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                      : 'text-gray-300 hover:text-white hover:bg-white/10'
                  }`}
                >
                  Diensten
                  <i className={`ri-arrow-down-s-line text-lg transition-transform duration-300 ${showMegaMenu ? 'rotate-180' : ''}`}></i>
                </Link>

                {/* Mega Menu */}
                <div 
                  className={`absolute top-full left-1/2 -translate-x-1/2 mt-4 transition-all duration-300 z-50 ${
                    showMegaMenu ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-4'
                  }`}
                >
                  <div className="bg-black backdrop-blur-xl border-2 border-cyan-500/30 rounded-2xl shadow-2xl shadow-cyan-500/30 p-6 w-[600px]">
                    <div className="grid grid-cols-2 gap-6">
                      {megaMenuServices.map((category, index) => (
                        <Link
                          key={index}
                          to={category.link}
                          className="group relative p-5 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-cyan-500/50 transition-all duration-300 cursor-pointer"
                        >
                          <div className={`absolute -inset-1 bg-gradient-to-br ${category.color} opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-300 rounded-xl`}></div>
                          
                          <div className="relative">
                            <div className="flex items-center gap-3 mb-4">
                              <div className={`w-10 h-10 flex items-center justify-center bg-gradient-to-br ${category.color} rounded-lg`}>
                                <i className={`${category.icon} text-xl text-white`}></i>
                              </div>
                              <h3 className="text-white font-bold text-base">{category.category}</h3>
                            </div>

                            <ul className="space-y-2">
                              {category.services.map((service, idx) => (
                                <li key={idx} className="flex items-center gap-2 text-gray-300 text-sm group-hover:text-white transition-colors duration-300">
                                  <i className={`${service.icon} text-cyan-400 text-xs`}></i>
                                  <span>{service.name}</span>
                                </li>
                              ))}
                            </ul>

                            <div className="mt-4 flex items-center gap-2 text-cyan-400 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                              <span>Bekijk meer</span>
                              <i className="ri-arrow-right-line"></i>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-6 pt-6 border-t border-white/10">
                      <Link
                        to="/diensten"
                        className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-cyan-500/50 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
                      >
                        <span>Bekijk Alle Diensten</span>
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>

              <Link
                to="/realisaties"
                className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 hover:scale-105 whitespace-nowrap ${
                  location.pathname === '/realisaties'
                    ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                Realisaties
              </Link>

              <Link
                to="/over-ons"
                className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 hover:scale-105 whitespace-nowrap ${
                  location.pathname === '/over-ons'
                    ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                Over Ons
              </Link>

              <Link
                to="/contact"
                className={`px-5 py-2.5 rounded-xl font-medium transition-all duration-300 hover:scale-105 whitespace-nowrap ${
                  location.pathname === '/contact'
                    ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                    : 'text-gray-300 hover:text-white hover:bg-white/10'
                }`}
              >
                Contact
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={toggleMobileMenu}
              className="lg:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors duration-300 cursor-pointer"
              aria-label="Menu"
            >
              <i className={`text-2xl transition-transform duration-300 ${isMobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Animated bottom border */}
        <div className={`h-px bg-gradient-to-r from-transparent via-cyan-500 to-transparent transition-opacity duration-500 ${
          isScrolled ? 'opacity-100' : 'opacity-0'
        }`}></div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div 
        className={`fixed inset-0 bg-black/80 backdrop-blur-sm z-30 lg:hidden transition-opacity duration-300 ${
          isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        onClick={toggleMobileMenu}
      ></div>

      {/* Mobile Menu */}
      <div 
        className={`fixed top-0 right-0 h-full w-full sm:w-80 bg-black/95 backdrop-blur-xl z-40 lg:hidden transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full pt-24 sm:pt-32 px-6">
          {/* Mobile Navigation Links */}
          <div className="flex flex-col space-y-2">
            <Link
              to="/"
              className={`px-6 py-4 rounded-xl font-medium transition-all duration-300 ${
                location.pathname === '/'
                  ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center space-x-3">
                <i className="ri-home-line text-xl"></i>
                <span>Home</span>
              </div>
            </Link>

            <Link
              to="/diensten"
              className={`px-6 py-4 rounded-xl font-medium transition-all duration-300 ${
                location.pathname === '/diensten'
                  ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center space-x-3">
                <i className="ri-tools-line text-xl"></i>
                <span>Diensten</span>
              </div>
            </Link>

            <Link
              to="/realisaties"
              className={`px-6 py-4 rounded-xl font-medium transition-all duration-300 ${
                location.pathname === '/realisaties'
                  ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center space-x-3">
                <i className="ri-gallery-line text-xl"></i>
                <span>Realisaties</span>
              </div>
            </Link>

            <Link
              to="/over-ons"
              className={`px-6 py-4 rounded-xl font-medium transition-all duration-300 ${
                location.pathname === '/over-ons'
                  ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center space-x-3">
                <i className="ri-team-line text-xl"></i>
                <span>Over Ons</span>
              </div>
            </Link>

            <Link
              to="/contact"
              className={`px-6 py-4 rounded-xl font-medium transition-all duration-300 ${
                location.pathname === '/contact'
                  ? 'text-white bg-gradient-to-r from-cyan-500 to-blue-600 shadow-lg shadow-cyan-500/30'
                  : 'text-gray-300 hover:text-white hover:bg-white/10'
              }`}
            >
              <div className="flex items-center space-x-3">
                <i className="ri-mail-line text-xl"></i>
                <span>Contact</span>
              </div>
            </Link>
          </div>

          {/* Mobile CTA Button */}
          <div className="mt-8">
            <a
              href="tel:0489170592"
              className="flex items-center justify-center space-x-3 px-6 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-bold rounded-xl shadow-lg shadow-cyan-500/30 hover:shadow-cyan-500/50 transition-all duration-300 cursor-pointer"
            >
              <i className="ri-phone-line text-xl"></i>
              <span>0489 17 05 92</span>
            </a>
          </div>

          {/* Mobile Menu Footer */}
          <div className="mt-auto pb-8 text-center">
            <p className="text-gray-400 text-sm">
              Antwerpen's #1 Auto-Elektronica
            </p>
          </div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white p-3 rounded-full shadow-lg hover:shadow-orange-500/50 transition-all duration-300 transform hover:scale-110 z-50 group cursor-pointer"
          aria-label="Scroll naar boven"
        >
          <i className="ri-arrow-up-line text-xl group-hover:animate-bounce"></i>
        </button>
      )}
    </>
  );
}
